import { registerApplication, start, navigateToUrl, addErrorHandler, getAppStatus, LOAD_ERROR, getAppNames, LOADING_SOURCE_CODE } from "single-spa";
import { SharedSettings } from "@pp/shared-module";
import data from "./assets/config/config-default-modules.json";

import {
	constructApplications,
	constructRoutes,
	constructLayoutEngine
} from "single-spa-layout";

// In order to make bootstrap works
import * as bootstrap from "bootstrap";
(<any>window).bootstrap = bootstrap;

import "bootstrap/dist/css/bootstrap.min.css";

import * as singleSpa from "single-spa";
(<any>window).singleSpa = singleSpa;

/**
* Returns a function to show the page when the location is any of those defined in routes 
* @param {any} routes
* @return {(location: any) => any} showWhenAnyOf
*/
function showWhenAnyOf(routes) {
	return function (location) {
		return routes.some((route) => location.pathname === route);
	};
}

/**
* Returns a function to show the page when the location pathname starts with any of those defined in routes 
* @param {any} routes
* @return {(location: any) => any} showWhenPrefix
*/
function showWhenPrefix(routes) {
	return function (location) {
		return routes.some((route) => location.pathname.startsWith(route));
	};
}

/**
* Returns a function to show the page when the location is none of those defined in routes 
* @param {any} routes
* @return {(location: any) => any} showExcept
*/
function showExcept(routes) {
	return function (location) {
		return !routes.every((route) => location.pathname.includes(route));
	};
}

const appLoginRoute = SharedSettings.LOGIN_ROUTE ?? "app/login";
const defaultModules = data?.defaultModules.filter((dm) => dm.addToRouter && dm.Code && dm.BaseClientUrl);
const basicRouter = createSingleSpaRouter(defaultModules);
const routes = constructRoutes(basicRouter);
let currentResolvedRoutes = routes;
let basicRoutesCount = parsedResolvedRoutes().length;

const applications = constructApplications({
	routes,
	loadApp({ name }) {
		return System.import(name);
	},
});

var layoutEngine = constructLayoutEngine({ routes, applications });
// Register all apps 
applications.forEach((app) => {
	if (!getAppNames().includes(app.name)) {
		registerApplication(app)
	}
});

layoutEngine.activate();
start({
	urlRerouteOnly: false
});

/**
* Added error handler function on microapps 
* @param {AppError} err
*/
addErrorHandler(err => {
	if (
		(getAppStatus && (getAppStatus(err.appOrParcelName) === LOAD_ERROR || getAppStatus(err.appOrParcelName) === LOADING_SOURCE_CODE)) || 
		(err.message.trim() !== "" && (err.message.includes(LOAD_ERROR) || err.message.includes(LOADING_SOURCE_CODE)))
	) {
		// Handle an app loading error

		// Define returnUrl to redirect to after user login
		let returnUrl = undefined;

		const queryString = window.location.search;
		if (queryString.trim().length !== 0) {
			const urlParams = new URLSearchParams(queryString);
			const url = urlParams.get("returnUrl");

			if (url && url.trim().length !== 0) {
				returnUrl = url;
			}
		}

		if (returnUrl === undefined && window.location.pathname !== "" && window.location.pathname !== "/") {
			returnUrl = window.location.pathname;
		}

		// Import the shared module to retrieve the authentication service
		System.import("@pp/shared-module")
			.then((sharedModule) => {
				// If the user is not logged in, a redirection is made to the login page keeping track of the source url
				if (!sharedModule || !sharedModule.useAuthService || !sharedModule.useAuthService.hasValidAccessToken()) {
					navigateToUrl(`/${appLoginRoute}${returnUrl ? `?returnUrl=${encodeURI(returnUrl)}` : ""}`);
				}
				// If the user is logged in and app is not in the modules list, a redirection is made to the error page keeping track of the source url
				else if (sharedModule && sharedModule.useAuthService && sharedModule.useAuthService.hasValidAccessToken() && !sharedModule.useAuthService.checkIfModuleExist(err.appOrParcelName)) {
					navigateToUrl(`/app/moduleError${returnUrl ? `?returnUrl=${encodeURI(returnUrl)}` : ""}`);
				}
			});
	}
})

/**
 * Event listener for single-spa routing event
 */
window.addEventListener("single-spa:routing-event", (evt: any) => {
	additionalOperationOnRoutingEvent();
	if (!evt.detail) { return; }

	const popStateEvent = evt.detail.originalEvent;

	if (!popStateEvent || !(popStateEvent instanceof PopStateEvent)) { return; }

	// When popStateEvent is defined and is an instance of PopStateEvent, the routing event source is internal to single-spa app.
	if (evt.detail.appsByNewStatus[LOAD_ERROR] && evt.detail.appsByNewStatus[LOAD_ERROR].length > 0) {
		// If there is an app loading error, a redirection is made to the error page
		navigateToUrl(`/${appLoginRoute}`);
	}
});

window.addEventListener("single-spa:before-routing-event", (evt: any) => {
	const resolvedRoutes = parsedResolvedRoutes();
	const resolvedRoutesPaths = resolvedRoutes.filter(r => r.path).map(r => r.path as string);
	let currentPath = window.location.pathname;
	let firstPathElement = undefined;
	
	if (currentPath && currentPath !== "/") {
		if (currentPath[0] === "/") {
			currentPath = currentPath.slice(1); 
		}
		
		firstPathElement = currentPath.split("/")?.[0];
	}

	if (
		basicRoutesCount < resolvedRoutes.length && 
		currentPath !== "/" &&
		firstPathElement && firstPathElement.trim().length > 0 && 
		!resolvedRoutesPaths.includes(firstPathElement)
	) {
		// when route does not exist, canel navigation and redirect to 404 page
		evt?.detail?.cancelNavigation();
		navigateToUrl("/app/404");
		return;
	}

	System.import("@pp/shared-module").then((sharedModule) => {
		if (
			basicRoutesCount === resolvedRoutes.length && 
			currentPath !== "/" && 
			firstPathElement && firstPathElement.trim().length > 0 && 
			!resolvedRoutesPaths.includes(firstPathElement) &&
			(sharedModule && sharedModule.useAuthService && !sharedModule.useAuthService.hasValidAccessToken())
		) {
			let returnUrl = undefined;

			const queryString = window.location.search;
			if (queryString.trim().length !== 0) {
				const urlParams = new URLSearchParams(queryString);
				const url = urlParams.get("returnUrl");

				if (url && url.trim().length !== 0) {
					returnUrl = url;
				}
			}

			if (returnUrl === undefined && window.location.pathname !== "" && window.location.pathname !== "/") {
				returnUrl = window.location.pathname;
			}

			evt?.detail?.cancelNavigation();
			navigateToUrl(`/${appLoginRoute}${returnUrl ? `?returnUrl=${encodeURI(returnUrl)}` : ""}`);
			return;
		}

		if (!sharedModule || !sharedModule.useGlobalLoaderService) { return; }
	
		const min = Math.ceil(0);
		const max = Math.floor(15);
	
		sharedModule.useGlobalLoaderService.showRoutingLoader({ value: Math.floor(Math.random() * (max - min + 1) + min) });
	})
});

async function additionalOperationOnRoutingEvent() {
	const sharedModule = await System.import("@pp/shared-module");

	if (!sharedModule || !sharedModule.useAuthService || sharedModule.useAuthService.hasValidAccessToken()) {
		document?.body?.classList.add("loggedin");
	}
	else {
		document?.body?.classList.remove("loggedin");
	}

	if (!sharedModule || !sharedModule.useGlobalLoaderService) { return; }

	sharedModule.useGlobalLoaderService.hideRoutingLoader();
}

window.addEventListener("registerPPApps", (event: CustomEvent) => {
	const router = createSingleSpaRouter(event.detail);
	const routes = constructRoutes(router);
	currentResolvedRoutes = routes;

	const applications = constructApplications({
		routes,
		loadApp({ name }) {
			return System.import(name);
		},
	});

	layoutEngine = constructLayoutEngine({ routes, applications });
	applications.forEach((app) => {
		if (!getAppNames().includes(app.name)) {
			registerApplication(app)
		}
	});
})

function createSingleSpaRouter(additionalRoutes?: Array<any>): string {
	const indexRedirectRoute = SharedSettings.DEFAULT_HOME_ROUTE ?? "app/home";

	const router = 
		`<single-spa-router>
			<div class="main-layout">
				<nav class="sidemenu">
					<application name="@pp/sidemenu"></application>
				</nav>

				<div class="layout-content" role="main" id="routeContainer">
					<redirect from="/" to="${indexRedirectRoute}"></redirect>

					<route default>
					</route>

					<route path="app">
						<application name="@pp/core"></application>
					</route>${
			additionalRoutes ? additionalRoutes.map((item) => {
				if (item.Code && item.BaseClientUrl) {
					return `

					<route path="${item.Code}">
						<application name="@pp/${item.Code}"></application>
					</route>`
				}
			}).filter(item => item).join("\n") : "" }
					
					<route path="forbidden">
					</route>
					
					<route path="error">
						<div>
							Error
						</div>
					</route>
				</div>
			</div>
		</single-spa-router>`;
	return router;
}

function parsedResolvedRoutes(): Array<any> {
	let rawRoutes = flattenObjsByProperty(currentResolvedRoutes.routes, "routes");
	return rawRoutes.filter(r => r.type && r.type === "route") ?? [...[]];
}

function flattenObjsByProperty(arr: Array<any>, property: string): Array<any> {
	if (!arr || !arr.length) return [];

	const dynamicKey = `${property}`;
	return arr.flatMap((item: any) => {
		return [
			item,
			...flattenObjsByProperty(item[dynamicKey] ?? [], property),
		]
	});
}